import type { Locale } from "@/core/i18n/locale"

/**
 * Words the contact form prints, per site language (D-WEB-LANG-SCOPE). The server and the zod schema
 * answer in English; `localizeMessage` turns each known message into Chinese on the client and leaves
 * an unknown one as it is. Add the Chinese here whenever an English message is added to `send.ts` or
 * `schema.ts` (`contact-text.test.ts` fails when one has no Chinese text).
 */
export interface ContactText {
  name: string
  email: string
  message: string
  send: string
  sent: string
}

export const CONTACT_TEXT: Record<Locale, ContactText> = {
  en: {
    name: "Your name",
    email: "Your email",
    message: "How can we help?",
    send: "Send message",
    sent: "Message sent",
  },
  zh: {
    name: "你的姓名",
    email: "你的邮箱",
    message: "有什么可以帮你？",
    send: "发送消息",
    sent: "消息已发送",
  },
}

const MESSAGES_ZH: Record<string, string> = {
  Required: "必填",
  "Remove line breaks": "请去掉换行",
  "Enter a valid email address": "请输入有效的邮箱地址",
  "Write at least 10 characters": "请至少写 10 个字符",
  "Thanks, your message is on its way. We will reply by email.":
    "谢谢，你的消息已发出。我们会通过邮件回复你。",
  "That was quick. Please check your message and send it again.":
    "提交得太快了。请检查你的消息后再发送一次。",
  "Too many messages from your network. Please try again later.":
    "你所在网络发来的消息太多，请稍后再试。",
  "Human verification failed. Please try again.": "人机验证没有通过，请再试一次。",
  "We could not send your message. Please try again in a few minutes.":
    "消息没能发出，请几分钟后再试。",
}

/** Every English message the form can show that has a Chinese text (for the test). */
export const KNOWN_MESSAGES: readonly string[] = Object.keys(MESSAGES_ZH)

export function localizeMessage(message: string, locale: Locale): string {
  if (locale === "en") return message
  const max = /^Use at most (\d+) characters$/.exec(message)
  if (max) return `最多 ${max[1]} 个字符`
  return MESSAGES_ZH[message] ?? message
}
